import { Search } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import type { SupplyProduct } from '@obraplan/core/application/ports/requisition';
import { Input } from '@/components/ui/input';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { useLive } from '@/lib/use-live';
import { cn } from '@/lib/cn';

/**
 * Por que o catálogo de compras não serve agora, quando não serve.
 *
 * - `FORA_DO_AR`: a busca foi recusada — a integração existe e não respondeu;
 * - `SEM_CATALOGO`: a primeira página, sem termo nenhum, veio vazia — não há integração, ou
 *   o catálogo de lá está vazio. Com termo, vazio é só "não achou", e não entra aqui.
 */
export type CatalogoIndisponivel = 'FORA_DO_AR' | 'SEM_CATALOGO';

/**
 * O seletor de produto do catálogo de compras — o do "Novo item", e o do código do item que
 * já existe (Fase 29, RN-395).
 *
 * A ordem importa e é o ponto deste campo. Digitado à mão, o mesmo tijolo vira "TIJOLO
 * CERAMICO 14X19X29", "TIJOLO CERÂMICO FURADO 14x19x29" e "TIJ CER 14X19X29" — e a
 * consolidação, que une material pelo **código de compras** (RN-011), passa a somar três
 * linhas onde há uma. Escolhendo da lista, o item ganha o nome e o SKU que o almoxarifado
 * usa, e o pedido fecha.
 *
 * ## Escolher primeiro, digitar depois
 *
 * A lista **abre ao focar**, com a primeira página do catálogo, antes de qualquer termo.
 * É a diferença entre um campo de busca e um seletor, e ela decide se a tela serve: quem
 * não lembra a grafia exata do cadastro não tem como adivinhá-la, e adivinhar errado não
 * achava nada — o campo ficava mudo justamente para quem mais precisava dele. Digitar
 * passa a ser o que estreita a lista, e não o que a faz existir.
 *
 * As setas andam, Enter escolhe, Esc fecha: é o `role="combobox"` completo, porque um
 * seletor que só funciona no mouse não substitui o seletor que ele veio substituir.
 *
 * ## Quem usa diz o que fazer sem o catálogo
 *
 * O "Novo item" manda cadastrar à mão; o código do item existente só aceita o digitado com
 * o compras fora do ar (D9). Por isso as frases de "não achou" e de "falhou" vêm de fora, e
 * a indisponibilidade é avisada (`onIndisponivel`) para quem precisa abrir o campo digitado.
 */
export function BuscaNoCompras({
  onEscolher,
  abertaDeInicio = false,
  dicaSemAchado = 'Cadastre o material à mão abaixo.',
  dicaNaFalha = 'Você ainda pode cadastrar o material à mão abaixo.',
  onIndisponivel,
}: {
  onEscolher: (produto: SupplyProduct) => void;
  /**
   * A lista nasce aberta — no diálogo do código, abrir é a razão de ele existir, e esperar o
   * foco seria pedir um clique a mais para ver o catálogo.
   */
  abertaDeInicio?: boolean;
  /** O que fazer quando o termo não acha nada — vai depois de "Nada no catálogo para…". */
  dicaSemAchado?: string;
  /** O que fazer quando a busca falha — vai depois da mensagem da falha. */
  dicaNaFalha?: string;
  onIndisponivel?: (motivo: CatalogoIndisponivel) => void;
}) {
  const container = useContainer();
  const { session } = useSessionState();

  const [busca, setBusca] = useState('');
  const termo = useTermoAdiado(busca);
  const idDaBusca = useId();
  // A lista aberta, e qual opcao esta sob a seta. `undefined` = nenhuma, que e o estado
  // de quem acabou de abrir: a primeira seta escolhe a primeira, e nao a segunda.
  const [aberto, setAberto] = useState(abertaDeInicio);
  const [destacado, setDestacado] = useState<number | undefined>(undefined);

  const achados = useLive(
    () =>
      session === undefined || !aberto
        ? Promise.resolve(undefined)
        : container.searchSupplyProducts.execute(session, termo),
    // `aberto` entra nas dependências: sem ele, abrir um serviço já consultaria a base do
    // cliente, mesmo que ninguém fosse acrescentar item nenhum.
    [session?.userId, termo, aberto],
  );

  const produtos = achados?.ok === true ? achados.value : [];
  const falhou = achados?.ok === false ? achados.error : undefined;
  const procurando = aberto && achados === undefined;

  const indisponivel: CatalogoIndisponivel | undefined =
    falhou !== undefined
      ? 'FORA_DO_AR'
      : achados?.ok === true && produtos.length === 0 && termo.trim() === ''
        ? 'SEM_CATALOGO'
        : undefined;
  useEffect(() => {
    if (indisponivel !== undefined) onIndisponivel?.(indisponivel);
  }, [indisponivel, onIndisponivel]);

  const escolher = (produto: SupplyProduct): void => {
    onEscolher(produto);
    // A busca some ao escolher: o que interessa dali em diante é o item montado, e manter a
    // lista aberta convidaria a trocar por engano o que já foi decidido.
    setBusca('');
    setAberto(false);
    setDestacado(undefined);
  };

  /**
   * As setas andam pela lista, Enter escolhe, Esc fecha.
   *
   * É o que faz este campo se comportar como o seletor que ele substitui. Sem isto, a
   * lista só serviria ao mouse — e PLANO_GERAL §4.4 põe o teclado como caminho de
   * primeira classe, não como alternativa.
   */
  const aoTeclar = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape') {
      setAberto(false);
      setDestacado(undefined);
      return;
    }
    if (produtos.length === 0) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setAberto(true);
      setDestacado((atual) => {
        const passo = event.key === 'ArrowDown' ? 1 : -1;
        if (atual === undefined) return passo === 1 ? 0 : produtos.length - 1;
        return (atual + passo + produtos.length) % produtos.length;
      });
      return;
    }

    if (event.key === 'Enter' && destacado !== undefined) {
      // Só intercepta quando há uma opção sob a seta: sem isso, o Enter de quem digitou
      // o nome à mão seria engolido pela lista em vez de submeter o formulário.
      event.preventDefault();
      const escolhido = produtos[destacado];
      if (escolhido !== undefined) escolher(escolhido);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-content-muted)"
          aria-hidden="true"
        />
        <Input
          id={`${idDaBusca}-campo`}
          role="combobox"
          aria-label="Procurar material no catálogo de compras"
          aria-expanded={aberto}
          aria-controls={`${idDaBusca}-lista`}
          aria-autocomplete="list"
          {...(destacado === undefined
            ? {}
            : { 'aria-activedescendant': `${idDaBusca}-op-${String(destacado)}` })}
          placeholder="Escolha da lista, ou digite para filtrar — tijolo, cimento, E-685110…"
          className="h-9 pl-9"
          value={busca}
          onFocus={() => setAberto(true)}
          onChange={(event) => {
            setBusca(event.target.value);
            setAberto(true);
            setDestacado(undefined);
          }}
          onKeyDown={aoTeclar}
        />
      </div>

      {aberto && (
        <>
          {procurando && (
            <p className="text-xs text-(--color-content-muted)" role="status">
              Carregando o catálogo…
            </p>
          )}

          {/*
            A falha da integração é dita, e não engolida: quem não vê resposta precisa
            saber se o catálogo não tem o material ou se ele está fora do ar — no
            primeiro caso digita à mão com confiança, no segundo espera ou avisa
            alguém.
          */}
          {falhou !== undefined && (
            <p className="text-xs text-(--color-state-warn)">
              {`${falhou.message} ${dicaNaFalha}`.trim()}
            </p>
          )}

          {achados !== undefined && falhou === undefined && (
            <>
              {produtos.length === 0 ? (
                <p className="text-xs text-(--color-content-muted)">
                  {termo === ''
                    ? 'O catálogo de compras não devolveu nenhum material.'
                    : `Nada no catálogo para “${termo}”. ${dicaSemAchado}`.trim()}
                </p>
              ) : (
                <>
                  <ul
                    id={`${idDaBusca}-lista`}
                    role="listbox"
                    aria-label="Materiais do catálogo de compras"
                    className="flex max-h-56 flex-col gap-1 overflow-y-auto rounded-md border border-(--color-border-subtle) bg-(--color-surface-raised) p-1"
                  >
                    {produtos.map((produto, indice) => (
                      <li
                        key={produto.code}
                        id={`${idDaBusca}-op-${String(indice)}`}
                        role="option"
                        aria-selected={indice === destacado}
                      >
                        <button
                          type="button"
                          tabIndex={-1}
                          // `onMouseDown` e não `onClick`: o clique tira o foco do
                          // campo antes de disparar, e fechar a lista no `blur`
                          // engoliria a escolha.
                          onMouseDown={(event) => {
                            event.preventDefault();
                            escolher(produto);
                          }}
                          className={cn(
                            'flex w-full flex-wrap items-baseline justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs',
                            indice === destacado
                              ? 'bg-(--color-surface-muted)'
                              : 'hover:bg-(--color-surface-muted)',
                          )}
                        >
                          <span className="min-w-0 flex-1 truncate font-medium">
                            {produto.description}
                          </span>
                          <span className="tabular text-(--color-content-muted)">
                            {produto.code}
                            {produto.unit === undefined ? '' : ` · ${produto.unit}`}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  {produtos.length >= 50 && (
                    <p className="text-xs text-(--color-content-muted)">
                      Mostrando os 50 primeiros. Digite para estreitar a lista.
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

/**
 * O termo, atrasado.
 *
 * Sem isto, cada tecla vira uma requisição à base do cliente: escrever "tijolo" dispararia
 * seis buscas, das quais só a última interessa. O atraso é curto o bastante para não
 * parecer travado e longo o bastante para uma palavra inteira caber num só pedido.
 */
function useTermoAdiado(valor: string, atrasoMs = 300): string {
  const [adiado, setAdiado] = useState(valor);

  useEffect(() => {
    const id = setTimeout(() => setAdiado(valor), atrasoMs);
    return () => clearTimeout(id);
  }, [valor, atrasoMs]);

  return adiado;
}
